import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CompanyProvider } from "@/contexts/CompanyContext";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { NavigationProvider } from "@/contexts/NavigationContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import PriceList from "@/pages/PriceList";
import InventoryAdjustments from "@/pages/InventoryAdjustments";
import InventoryReceive from "@/pages/InventoryReceive";
import InventoryCounts from "@/pages/InventoryCounts";
import DoubleCostReport from "@/pages/DoubleCostReport";
import Warehouses from "@/pages/Warehouses";
import WarehouseTransfers from "@/pages/WarehouseTransfers";
import TransferRequests from "@/pages/TransferRequests";
import ExpirationAlerts from "@/pages/ExpirationAlerts";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import ClientImport from "@/pages/ClientImport";
import DormantClients from "@/pages/DormantClients";
import CreditBlocks from "@/pages/CreditBlocks";
import Representatives from "@/pages/Representatives";
import Sales from "@/pages/Sales";
import InvoiceNew from "@/pages/InvoiceNew";
import InvoicePrint from "@/pages/InvoicePrint";
import ShippingGuide from "@/pages/ShippingGuide";
import PaymentsAndReceivables from "@/pages/PaymentsAndReceivables";
import Suppliers from "@/pages/Suppliers";
import Purchases from "@/pages/Purchases";
import PurchaseNew from "@/pages/PurchaseNew";
import Payables from "@/pages/Payables";
import SupplierProfile from "@/pages/SupplierProfile";
import CreditNotes from "@/pages/CreditNotes";
import Treasury from "@/pages/Treasury";
import Accounting from "@/pages/Accounting";
import Reports from "@/pages/Reports";
import SalesTargets from "@/pages/SalesTargets";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";
import Companies from "@/pages/Companies";
import Imports from "@/pages/Imports";
import AutoOC from "@/pages/AutoOC";
import Commissions from "@/pages/Commissions";
import Expenses from "@/pages/Expenses";
import ExpenseAccounts from "@/pages/ExpenseAccounts";
import Loans from "@/pages/Loans";
import ExpenseDistribution from "@/pages/ExpenseDistribution";
import Deposits from "@/pages/Deposits";
import SARCompliance from "@/pages/SARCompliance";
import ModulePlaceholder from "@/pages/ModulePlaceholder";
import CalendarAlerts from "@/pages/CalendarAlerts";
import PromesaNueva from "@/pages/PromesaNueva";
import PromesaDetalle from "@/pages/PromesaDetalle";
import UsuarioRegistro from "@/pages/UsuarioRegistro";
import Permissions from "@/pages/Permissions";
import Regions from "@/pages/Regions";
import AdminDashboard from "@/pages/AdminDashboard";
import HumanResources from "@/pages/HumanResources";
import Forms from "@/pages/Forms";
import Prueba from "@/pages/Prueba";
import Landing from "@/pages/Landing";
import LandingEditor from "@/pages/LandingEditor";
import Backups from "@/pages/Backups";
import Templates from "@/pages/Templates";
import TemplateEditor from "@/pages/TemplateEditor";
import TemplatesByType from "@/pages/TemplatesByType";
import CompanySettingsPage from "@/pages/CompanySettings";
import PurchaseRequests from "@/pages/PurchaseRequests";
import NotaDescargaHost from "@/components/NotaDescarga";

function AppRoutes() {
  const { user } = useAuth();
  return (
    <CompanyProvider user={user}>
      <SidebarProvider>
        <BrowserRouter>
        <NavigationProvider userId={user?.id || ""}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Landing pública en la raíz */}
          <Route path="/" element={<Landing />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/productos" element={<ProtectedRoute modulo="inventario"><Products /></ProtectedRoute>} />
          <Route path="/productos/lista-precios" element={<ProtectedRoute modulo="inventario"><PriceList /></ProtectedRoute>} />
          <Route path="/productos/:id" element={<ProtectedRoute modulo="inventario"><ProductDetail /></ProtectedRoute>} />
          <Route path="/inventario/ajustes" element={<ProtectedRoute modulo="inventario"><InventoryAdjustments /></ProtectedRoute>} />
          <Route path="/inventario/ingresos" element={<ProtectedRoute modulo="inventario"><InventoryReceive /></ProtectedRoute>} />
          <Route path="/inventario/conteo-fisico" element={<ProtectedRoute modulo="inventario"><InventoryCounts /></ProtectedRoute>} />
          <Route path="/inventario/doble-lectura" element={<ProtectedRoute modulo="inventario"><DoubleCostReport /></ProtectedRoute>} />
          <Route path="/inventario/bodegas" element={<ProtectedRoute modulo="inventario" soloAdmin><Warehouses /></ProtectedRoute>} />
          <Route path="/inventario/transferencias" element={<ProtectedRoute modulo="inventario"><WarehouseTransfers /></ProtectedRoute>} />
          <Route path="/inventario/solicitudes-traslado" element={<ProtectedRoute modulo="inventario"><TransferRequests /></ProtectedRoute>} />
          <Route path="/inventario/alertas" element={<ProtectedRoute modulo="inventario"><ExpirationAlerts /></ProtectedRoute>} />
          <Route path="/clientes" element={<ProtectedRoute modulo="clientes"><Clients /></ProtectedRoute>} />
          <Route path="/clientes/importar" element={<ProtectedRoute modulo="clientes" soloAdmin><ClientImport /></ProtectedRoute>} />
          <Route path="/clientes/dormidos" element={<ProtectedRoute modulo="clientes"><DormantClients /></ProtectedRoute>} />
          <Route path="/clientes/bloqueos" element={<ProtectedRoute modulo="clientes"><CreditBlocks /></ProtectedRoute>} />
          <Route path="/clientes/:id" element={<ProtectedRoute modulo="clientes"><ClientDetail /></ProtectedRoute>} />
          <Route path="/rtc" element={<ProtectedRoute modulo="clientes"><Representatives /></ProtectedRoute>} />
          <Route path="/importaciones" element={<ProtectedRoute modulo="compras"><Imports /></ProtectedRoute>} />
          <Route path="/auto-oc" element={<ProtectedRoute modulo="compras"><AutoOC /></ProtectedRoute>} />
          <Route path="/cobros-rtc" element={<ProtectedRoute><PaymentsAndReceivables /></ProtectedRoute>} />
          <Route path="/comisiones" element={<ProtectedRoute modulo="comisiones"><Commissions /></ProtectedRoute>} />
          <Route path="/viaticos" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
          <Route path="/ventas" element={<ProtectedRoute modulo="ventas"><Sales /></ProtectedRoute>} />
          <Route path="/ventas/promesas/nueva" element={<ProtectedRoute modulo="ventas"><PromesaNueva /></ProtectedRoute>} />
          <Route path="/ventas/promesas/:id" element={<ProtectedRoute modulo="ventas"><PromesaDetalle /></ProtectedRoute>} />
          <Route path="/ventas/nueva" element={<ProtectedRoute modulo="ventas"><InvoiceNew /></ProtectedRoute>} />
          <Route path="/ventas/proforma/:id/editar" element={<ProtectedRoute modulo="ventas"><InvoiceNew /></ProtectedRoute>} />
          <Route path="/ventas/factura/:id/corregir" element={<ProtectedRoute modulo="ventas" soloAdmin><InvoiceNew corregir /></ProtectedRoute>} />
          <Route path="/proformas" element={<Navigate to="/ventas" replace />} />
          <Route path="/ventas/:id/imprimir" element={<ProtectedRoute modulo="ventas"><InvoicePrint /></ProtectedRoute>} />
          <Route path="/ventas/:id/guia" element={<ProtectedRoute modulo="ventas"><ShippingGuide /></ProtectedRoute>} />
          <Route path="/cxc" element={<ProtectedRoute modulo="cobranza"><PaymentsAndReceivables /></ProtectedRoute>} />
          <Route path="/proveedores" element={<ProtectedRoute modulo="compras"><Suppliers /></ProtectedRoute>} />
          <Route path="/proveedores/:id" element={<ProtectedRoute modulo="compras"><SupplierProfile /></ProtectedRoute>} />
          <Route path="/compras" element={<ProtectedRoute modulo="compras"><Purchases /></ProtectedRoute>} />
          <Route path="/compras/oc/nueva" element={<ProtectedRoute modulo="compras"><PurchaseNew mode="order" /></ProtectedRoute>} />
          <Route path="/compras/factura/nueva" element={<ProtectedRoute modulo="compras"><PurchaseNew mode="invoice" /></ProtectedRoute>} />
          <Route path="/compras/factura/:id/editar" element={<ProtectedRoute modulo="compras"><PurchaseNew mode="invoice" /></ProtectedRoute>} />
          <Route path="/cxp" element={<ProtectedRoute modulo="compras"><Payables /></ProtectedRoute>} />
          <Route path="/notas-credito" element={<ProtectedRoute modulo="contabilidad"><CreditNotes /></ProtectedRoute>} />
          <Route path="/tesoreria" element={<ProtectedRoute modulo="contabilidad"><Treasury /></ProtectedRoute>} />
          <Route path="/contabilidad" element={<ProtectedRoute modulo="contabilidad"><Accounting /></ProtectedRoute>} />
          <Route path="/partidas-gasto" element={<ProtectedRoute modulo="contabilidad"><ExpenseAccounts /></ProtectedRoute>} />
          <Route path="/prestamos" element={<ProtectedRoute modulo="contabilidad"><Loans /></ProtectedRoute>} />
          <Route path="/gasto-distribuido" element={<ProtectedRoute modulo="contabilidad"><ExpenseDistribution /></ProtectedRoute>} />
          <Route path="/depositos" element={<ProtectedRoute modulo="contabilidad"><Deposits /></ProtectedRoute>} />
          {/* Las notas de débito se gestionan en Ventas y facturación (enlaces anteriores redirigen) */}
          <Route path="/notas-debito" element={<Navigate to="/ventas?tab=notas-debito" replace />} />
          <Route path="/sar" element={<ProtectedRoute modulo="contabilidad"><SARCompliance /></ProtectedRoute>} />
          <Route path="/empresas" element={<ProtectedRoute modulo="admin"><Companies /></ProtectedRoute>} />
          <Route path="/regiones" element={<ProtectedRoute modulo="admin" soloAdmin><Regions /></ProtectedRoute>} />
          <Route path="/reportes" element={<ProtectedRoute modulo="reportes"><Reports /></ProtectedRoute>} />
          <Route path="/centro-reportes" element={<Navigate to="/reportes?tab=otros" replace />} />
          <Route path="/metas" element={<ProtectedRoute modulo="ventas"><SalesTargets /></ProtectedRoute>} />
          <Route path="/auditoria" element={<ProtectedRoute modulo="admin"><Audit /></ProtectedRoute>} />
          <Route path="/configuracion" element={<ProtectedRoute modulo="admin"><Settings /></ProtectedRoute>} />
          {/* Módulos del PRD pendientes de implementación */}
          <Route path="/calendario" element={<ProtectedRoute modulo="calendario"><CalendarAlerts /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute modulo="admin"><AdminDashboard /></ProtectedRoute>} />
          <Route path="/permisos" element={<ProtectedRoute modulo="permisos"><Permissions /></ProtectedRoute>} />
          <Route path="/permisos/usuarios/:id" element={<ProtectedRoute modulo="permisos"><UsuarioRegistro /></ProtectedRoute>} />
          <Route path="/rrhh" element={<ProtectedRoute><HumanResources /></ProtectedRoute>} />
          <Route path="/formularios" element={<ProtectedRoute modulo="formularios"><Forms /></ProtectedRoute>} />
          <Route path="/prueba" element={<ProtectedRoute><Prueba /></ProtectedRoute>} />
          <Route path="/admin/landing-editor" element={<ProtectedRoute modulo="admin"><LandingEditor /></ProtectedRoute>} />
          <Route path="/admin/backups" element={<ProtectedRoute modulo="admin"><Backups /></ProtectedRoute>} />
          <Route path="/admin/plantillas" element={<ProtectedRoute modulo="admin"><Templates /></ProtectedRoute>} />
          <Route path="/admin/plantillas/tipo/:tipo" element={<ProtectedRoute modulo="admin"><TemplatesByType /></ProtectedRoute>} />
          <Route path="/admin/plantillas/:tid" element={<ProtectedRoute modulo="admin"><TemplateEditor /></ProtectedRoute>} />
          <Route path="/solicitudes-compra" element={<ProtectedRoute><PurchaseRequests /></ProtectedRoute>} />
          <Route path="/admin/configuracion-documentos" element={<ProtectedRoute modulo="admin"><CompanySettingsPage /></ProtectedRoute>} />
          {/* ruta anterior: redirige al apartado unificado (enlaces guardados siguen funcionando) */}
          <Route path="/admin/configuracion-facturas" element={<Navigate to="/admin/configuracion-documentos" replace />} />
          {/* Catch-all: vuelve a la landing pública */}
          <Route path="*" element={<Landing />} />
        </Routes>
        </NavigationProvider>
      </BrowserRouter>
      </SidebarProvider>
    </CompanyProvider>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AppRoutes />
        <NotaDescargaHost />
        <Toaster theme="dark" position="top-right" toastOptions={{
          style: { background: "#111612", border: "1px solid #222D26", color: "#F0F4F1" }
        }} />
      </AuthProvider>
    </div>
  );
}

export default App;
